import { gql, useMutation } from "@apollo/client";

import {
  CreatePublicSharedCollectionInput,
  ExternalLink,
} from "../../../generated/gql/graphql";

const CREATE_EXTERNAL_LINK = gql`
  mutation CreatePublicSharedCollection(
    $input: CreatePublicSharedCollectionInput!
  ) {
    createPublicSharedCollection(input: $input) {
      suspended
      startDate
      id
      expirationDate
      createdBy
      collectionId
    }
  }
`;

const DELETE_EXTERNAL_LINK = gql`
  mutation DeletePublicSharedCollection(
    $deletePublicSharedCollectionId: String!
  ) {
    deletePublicSharedCollection(id: $deletePublicSharedCollectionId)
  }
`;

type ExternalLinksApi = {
  createExternalLink(
    input: CreatePublicSharedCollectionInput
  ): Promise<ExternalLink>;
  deleteExternalLink(id: string): Promise<void>;
};

export default function useExternalLinks(): ExternalLinksApi {
  const [createExternalLinkMut] =
    useMutation<ExternalLink>(CREATE_EXTERNAL_LINK);
  const [deleteExternalLinkMut] = useMutation(DELETE_EXTERNAL_LINK);

  async function createExternalLink(
    input: CreatePublicSharedCollectionInput
  ): Promise<ExternalLink> {
    try {
      const result = await createExternalLinkMut({
        variables: {
          input,
        },
      });

      if (result.errors || !result.data) {
        return Promise.reject(
          new Error(`Error while creating external link. ${result.errors}`)
        );
      }

      return result.data;
    } catch (error) {
      throw new Error(`Failed to create external link.`);
    }
  }

  async function deleteExternalLink(id: string): Promise<void> {
    if (!id) {
      throw new Error(`Failed to delete external link. Id is required`);
    }

    try {
      const result = await deleteExternalLinkMut({
        variables: {
          deletePublicSharedCollectionId: id,
        },
      });

      if (result.errors) {
        return Promise.reject(
          new Error(`Error while deleting external link '${id}'.`)
        );
      }
    } catch (error) {
      throw new Error(`Error while deleting external link with id '${id}'.`);
    }
  }

  return {
    createExternalLink,
    deleteExternalLink,
  };
}
